/**
 * SyncQueue Context
 * 동기화 큐 상태를 앱 전역에서 공유하기 위한 Context
 *
 * 문제: SyncButton, SyncPage, ProjectListPage가 각각 독립적인 useSyncQueue() 인스턴스를
 * 사용하여 pendingCount 상태가 공유되지 않았음.
 * 해결: 단일 Context Provider로 상태를 공유하여 동기화 완료 후 모든 소비자의 상태가 즉시 갱신됨.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useApolloClient } from "@apollo/client";
import { createLogger } from "@ict/shared";
import { UPDATE_MACHINE_DOCUMENT_ITEMS_MUTATION } from "@/lib/graphql/inspection";
import {
  ADD_MEASURE_MUTATION,
  UPDATE_MEASURE_MUTATION,
  DELETE_MEASURE_MUTATION,
} from "@/lib/graphql/measures";
import {
  DELETE_EQUIPMENT_MUTATION,
  executeEquipmentDeleteMutation,
  MACHINE_DOCUMENT_VERSION_QUERY,
  UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION,
  type UpdateMachineDocumentPicturesData,
  type UpdateMachineDocumentPicturesVariables,
} from "@/lib/graphql/equipment";
import { executeSyncQueue, syncPhotos, type SyncExecutionResult } from "@/lib/db/sync";
import {
  createPhotoEtcUploadedHandler,
  createMeasurePhotoUploadedHandler,
} from "@/lib/sync/photoCallbacks";
import { getPendingSyncCount, getSyncQueue, cleanupCompletedItems } from "@/lib/db/changes";
import {
  registerBackgroundSync,
  onBackgroundSyncTrigger,
  isBackgroundSyncSupported,
} from "@/lib/backgroundSync";
import type { SyncQueueItem } from "@/lib/db/types";
import { fetchMachineDocument as fetchMachineDocumentUtil } from "@/lib/sync/fetchMachineDocument";

/** SyncExecutionResult에서 UI 표시에 필요한 필드만 추출 */
type SyncResult = Pick<
  SyncExecutionResult,
  "success" | "syncedCount" | "failedCount" | "conflictCount" | "conflicts"
>;

interface SyncState {
  isSyncing: boolean;
  progress: number;
  total: number;
  currentItem: string | null;
  currentItemType: SyncQueueItem["type"] | null;
  lastSyncResult: SyncResult | null;
  error: string | null;
}

interface SyncQueueContextValue extends SyncState {
  pendingCount: number;
  backgroundSyncSupported: boolean;
  sync: () => Promise<SyncResult>;
  refreshPendingCount: () => Promise<number>;
  getQueue: () => Promise<SyncQueueItem[]>;
  cleanupOldCompleted: () => Promise<number>;
}

const SyncQueueContext = createContext<SyncQueueContextValue | null>(null);

const logger = createLogger({ module: "SyncQueueContext" });

export function SyncQueueProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SyncState>({
    isSyncing: false,
    progress: 0,
    total: 0,
    currentItem: null,
    currentItemType: null,
    lastSyncResult: null,
    error: null,
  });

  const [pendingCount, setPendingCount] = useState(0);
  const [backgroundSyncSupported] = useState(isBackgroundSyncSupported);

  // useRef로 isSyncing 추적 (리스너 재등록 방지)
  const isSyncingRef = useRef(false);
  isSyncingRef.current = state.isSyncing;

  const apolloClient = useApolloClient();

  // Mutations
  const [updateMachineDocumentItems] = useMutation(UPDATE_MACHINE_DOCUMENT_ITEMS_MUTATION);
  const [deleteEquipmentMutation] = useMutation(DELETE_EQUIPMENT_MUTATION);
  const [updatePictures] = useMutation<
    UpdateMachineDocumentPicturesData,
    UpdateMachineDocumentPicturesVariables
  >(UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION);
  const [addMeasureMutation] = useMutation(ADD_MEASURE_MUTATION);
  const [updateMeasureMutation] = useMutation(UPDATE_MEASURE_MUTATION);
  const [deleteMeasureMutation] = useMutation(DELETE_MEASURE_MUTATION);

  /**
   * 대기 중인 항목 수 갱신
   */
  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingSyncCount();
    setPendingCount(count);

    // 대기 항목이 있으면 BackgroundSync 등록
    if (count > 0 && backgroundSyncSupported) {
      registerBackgroundSync();
    }

    return count;
  }, [backgroundSyncSupported]);

  /**
   * BackgroundSync 메시지 리스너 설정
   * SW에서 BACKGROUND_SYNC_TRIGGER 메시지를 받으면 자동 동기화
   */
  useEffect(() => {
    if (!backgroundSyncSupported) return;

    const cleanup = onBackgroundSyncTrigger(async (itemCount) => {
      logger.info(`BackgroundSync 트리거: ${itemCount}개 항목`);
      if (!isSyncingRef.current) {
        await refreshPendingCount();
      }
    });

    return cleanup;
  }, [backgroundSyncSupported, refreshPendingCount]);

  /**
   * 동기화 큐 조회
   */
  const getQueue = useCallback(async (): Promise<SyncQueueItem[]> => {
    return getSyncQueue();
  }, []);

  /**
   * 오래된 완료 항목 자동 정리 (72시간 경과)
   */
  const cleanupOldCompleted = useCallback(async () => {
    const deleted = await cleanupCompletedItems();
    if (deleted > 0) {
      logger.info(`오래된 완료 항목 ${deleted}건 정리`);
    }
    return deleted;
  }, []);

  // 사진 동기화 콜백 (공유 유틸리티 사용)
  const handlePhotoEtcUploaded = useMemo(
    () => createPhotoEtcUploadedHandler(apolloClient, updatePictures),
    [apolloClient, updatePictures]
  );

  const handleMeasurePhotoUploaded = useMemo(
    () => createMeasurePhotoUploadedHandler(apolloClient, updateMeasureMutation),
    [apolloClient, updateMeasureMutation]
  );

  /**
   * 동기화 실행
   * photo 타입은 syncPhotos()로, 나머지는 executeSyncQueue()로 처리
   */
  const sync = useCallback(async (): Promise<SyncResult> => {
    setState((prev) => ({
      ...prev,
      isSyncing: true,
      progress: 0,
      error: null,
    }));

    try {
      const queue = await getSyncQueue();
      const total = queue.length;

      setState((prev) => ({ ...prev, total }));

      if (total === 0) {
        const result: SyncResult = {
          success: true,
          syncedCount: 0,
          failedCount: 0,
          conflictCount: 0,
          conflicts: [],
        };
        setState((prev) => ({
          ...prev,
          isSyncing: false,
          lastSyncResult: result,
        }));
        return result;
      }

      // mutation 래퍼: MutateFunction 타입에 맞게 newVersion 반환
      // measure 타입은 measureOperation 필드로 명시적 라우팅, 나머지는 updateMachineDocumentItems
      const mutate = async (opts: {
        variables: Record<string, unknown>;
      }): Promise<{ newVersion?: number }> => {
        const input = opts.variables.input as Record<string, unknown> | undefined;

        // #457: equipment delete 라우팅 (공유 유틸리티 사용)
        const equipmentOperation = input?.equipmentOperation as string | undefined;
        if (equipmentOperation === "delete") {
          return executeEquipmentDeleteMutation(input!, deleteEquipmentMutation);
        }

        const measureOperation = input?.measureOperation as string | undefined;

        if (measureOperation) {
          // measureOperation은 서버 전송 전 제거 (서버 스키마에 없는 필드)
          const { measureOperation: _op, ...serverInput } = input!;

          let mutationFn = updateMeasureMutation;
          let responseKey = "updateMeasure";

          if (measureOperation === "create") {
            mutationFn = addMeasureMutation;
            responseKey = "addMeasure";
          } else if (measureOperation === "delete") {
            mutationFn = deleteMeasureMutation;
            responseKey = "deleteMeasure";
          }

          // measureOperation 제거한 서버 전송용 변수 사용
          const serverOpts = { variables: { input: serverInput } };
          const mutationResult = await mutationFn(serverOpts);
          if (mutationResult.errors) {
            throw new Error(mutationResult.errors[0]?.message || "GraphQL error");
          }
          const response = (mutationResult.data as Record<string, unknown>)?.[responseKey] as
            | { success?: boolean; error?: string; errorCode?: string; newVersion?: number }
            | undefined;

          // VERSION_CONFLICT 발생 시 서버 버전 조회 후 1회 재시도
          if (response && !response.success && response.errorCode === "VERSION_CONFLICT") {
            logger.warn("measure VERSION_CONFLICT - 서버 버전 조회 후 재시도", { responseKey });
            try {
              const { data: versionData } = await apolloClient.query({
                query: MACHINE_DOCUMENT_VERSION_QUERY,
                variables: {
                  projectId: serverInput.projectId,
                  docKey: serverInput.docKey,
                  machineId: serverInput.machineId,
                },
                fetchPolicy: "network-only",
              });
              const serverVersion = versionData?.machineDocument?.version;
              if (serverVersion != null) {
                const retryOpts = {
                  variables: {
                    input: {
                      ...serverInput,
                      expectedVersion: serverVersion,
                      clientMutationId: `sync-retry-${Date.now()}`,
                    },
                  },
                };
                const retryResult = await mutationFn(retryOpts);
                if (retryResult.errors) {
                  throw new Error(retryResult.errors[0]?.message || "GraphQL error");
                }
                const retryResponse = (retryResult.data as Record<string, unknown>)?.[
                  responseKey
                ] as typeof response;
                if (retryResponse && !retryResponse.success) {
                  throw new Error(retryResponse.error || retryResponse.errorCode || "Retry failed");
                }
                return { newVersion: retryResponse?.newVersion };
              }
            } catch (retryError) {
              logger.error("measure VERSION_CONFLICT 재시도 실패:", retryError);
              throw retryError;
            }
            throw new Error("VERSION_CONFLICT: 서버 버전 조회 실패");
          }

          if (response && !response.success) {
            throw new Error(response.error || response.errorCode || "Mutation failed");
          }
          return { newVersion: response?.newVersion };
        }

        const mutationResult = await updateMachineDocumentItems(opts);
        if (mutationResult.errors) {
          throw new Error(mutationResult.errors[0]?.message || "GraphQL error");
        }
        const response = mutationResult.data?.updateMachineDocumentItems;
        if (response && !response.success) {
          throw new Error(response.error || response.errorCode || "Mutation failed");
        }
        return { newVersion: response?.newVersion };
      };

      // 설비 문서 조회 (itemId 매핑용, Feature 326)
      const fetchMachineDocument = (projectId: string, docKey: string, machineId: string) =>
        fetchMachineDocumentUtil(apolloClient, projectId, docKey, machineId);

      // GraphQL mutation 동기화를 먼저 실행 (equipment create, measure create 등)
      // 사진 동기화 시 서버에 measure/equipment가 존재해야 URL 업데이트 가능
      const result = await executeSyncQueue(
        mutate,
        fetchMachineDocument,
        undefined,
        false,
        undefined,
        {
          enableRetry: true,
          onItemProgress: (current: number, itemTotal: number, item: SyncQueueItem) => {
            setState((prev) => ({
              ...prev,
              progress: current,
              total: itemTotal,
              currentItem: item.id,
              currentItemType: item.type,
            }));
          },
        }
      );

      // 사진 동기화 (REST API 업로드 + measure/pictures URL 업데이트)
      // executeSyncQueue 이후 실행: 서버에 measure가 존재하는 상태에서 photo URL 업데이트
      const photoResult = await syncPhotos(handlePhotoEtcUploaded, handleMeasurePhotoUploaded);

      // 결과 합산
      const combinedResult: SyncResult = {
        success: result.success && photoResult.success,
        syncedCount: result.syncedCount + photoResult.syncedCount,
        failedCount: result.failedCount + photoResult.failedCount,
        conflictCount: result.conflictCount + photoResult.conflictCount,
        conflicts: [...result.conflicts, ...photoResult.conflicts],
      };

      setState((prev) => ({
        ...prev,
        isSyncing: false,
        progress: combinedResult.syncedCount,
        lastSyncResult: combinedResult,
      }));

      await refreshPendingCount();

      return combinedResult;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "동기화 실패";
      const errorResult: SyncResult = {
        success: false,
        syncedCount: 0,
        failedCount: 0,
        conflictCount: 0,
        conflicts: [],
      };
      setState((prev) => ({
        ...prev,
        isSyncing: false,
        error: errorMessage,
        lastSyncResult: errorResult,
      }));

      return errorResult;
    }
  }, [
    updateMachineDocumentItems,
    deleteEquipmentMutation,
    addMeasureMutation,
    updateMeasureMutation,
    deleteMeasureMutation,
    refreshPendingCount,
    apolloClient,
    handlePhotoEtcUploaded,
    handleMeasurePhotoUploaded,
  ]);

  const value: SyncQueueContextValue = {
    ...state,
    pendingCount,
    backgroundSyncSupported,
    sync,
    refreshPendingCount,
    getQueue,
    cleanupOldCompleted,
  };

  return <SyncQueueContext.Provider value={value}>{children}</SyncQueueContext.Provider>;
}

export function useSyncQueueContext() {
  const context = useContext(SyncQueueContext);
  if (!context) {
    throw new Error("useSyncQueueContext must be used within a SyncQueueProvider");
  }
  return context;
}
